import { backgrounds } from "assets/backgrounds";
import { DashboardContext } from "contexts/DashboardContext";
import { useWidgets, WIDGETS_WITHOUT_REMEMBER_SETTINGS } from "hooks/widgets";
import { Color } from "material-ui-color";
import { useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { DashboardProps } from "types/GlobalData";
import {
  findDashboardGroupById,
  getValidDashboards,
  sanitizeDashboardGroups,
} from "util/dashboardGroups";
import { getCurrentDashboardProps } from "util/getCurrentDashboard";
import { getMost } from "util/objects";

import { DashboardControls } from "./DashboardControls";
import { MultipleDashboardsBox } from "./multipleDashboard";
import { SettingsBar } from "./SettingsBar";
import { Spotlight } from "./Spotlight";
import { ToolbarContainer } from "./toolbar/ToolbarContainer";
import { Widget } from "./Widget";
import { WidgetErrorBoundary } from "./WidgetErrorBoundary";

interface Props {}

//The maximum z index to be reached before all z-indices are recalculated
const MAX_Z_INDEX = 100;

interface SettingsProps {
  settings: { [id: string]: any };
  settingsId: string;
  updateSettings: (setting: string, data: any) => void;
  state: { [id: string]: any };
}

const Dashboard: React.FC<Props> = () => {
  const {
    data: globalData,
    setData: updateGlobalData,
    wpLoadData,
    wpSaveData,
  } = useContext(DashboardContext);
  const {
    widgets,
    updateWidgetPositions,
    updateWidgetState,
    updateWidgetSetting,
    removeWidget,
    addWidget,
  } = useWidgets(wpLoadData, wpSaveData);

  const [maxZIndex, setMaxZIndex] = useState<number>(
    getMost(widgets, (widget) => widget.position?.zIndex) || 0,
  );
  const [focusedWidget, setFocusedWidget] = useState<string>();
  const [settingsWidget, setSettingsWidget] = useState<string>();
  const [spotlightWidget, setSpotlightWidget] = useState<string>();
  const [isSettingsOpen, setSettingsOpen] = useState<boolean>(false);
  const validGroups = useMemo(
    () => sanitizeDashboardGroups(globalData.dashboardGroups),
    [globalData.dashboardGroups],
  );
  const activeGroup = validGroups.find((group) => group.id === globalData.activeDashboardGroupId);
  const dashboards = activeGroup?.dashboards ?? [];
  const activeDashboard = getCurrentDashboardProps(globalData.activeDashboardId, dashboards);
  const shouldUpdateRememberSettings =
    focusedWidget !== undefined && widgets[focusedWidget]?.settings;

  // Ensure active dashboard state is properly set when component mounts
  useEffect(() => {
    if (dashboards.length > 0 && !globalData.activeDashboardId) {
      // If no active dashboard is set but dashboards exist, set the first one as active
      const firstDashboard = dashboards[0];
      updateGlobalData({
        activeDashboardId: firstDashboard.id,
      });
    }
  }, [dashboards, globalData.activeDashboardId, updateGlobalData]);

  // Ensure the active dashboard state is properly preserved when navigating back
  useEffect(() => {
    if (dashboards.length > 0) {
      // Check if the current active dashboard exists in the current dashboards list
      const currentActiveDashboard = dashboards.find((d) => d.id === globalData.activeDashboardId);

      if (currentActiveDashboard) {
        // Active dashboard exists in current dashboards, keep it
        console.log(
          "Dashboard: Active dashboard found in current dashboards:",
          globalData.activeDashboardId,
        );
      } else {
        // Active dashboard doesn't exist in current dashboards
        // First, check if it exists in any other dashboard group
        let foundInOtherGroup = false;
        for (const group of validGroups) {
          if (group.id !== globalData.activeDashboardGroupId) {
            const dashboardInOtherGroup = group.dashboards.find(
              (d) => d.id === globalData.activeDashboardId,
            );
            if (dashboardInOtherGroup) {
              // Found the active dashboard in another group, switch to that group
              console.log(
                "Dashboard: Active dashboard found in group",
                group.id,
                "switching active group",
              );
              updateGlobalData({
                activeDashboardGroupId: group.id,
              });
              foundInOtherGroup = true;
              break;
            }
          }
        }

        if (!foundInOtherGroup) {
          // Active dashboard doesn't exist anywhere, set to the last dashboard in current group
          const lastDashboard = dashboards[dashboards.length - 1];
          console.log(
            "Dashboard: Active dashboard not found anywhere, setting to last dashboard:",
            lastDashboard.id,
          );
          updateGlobalData({
            activeDashboardId: lastDashboard.id,
          });
        }
      }
    } else if (dashboards.length === 0) {
      // If there are no dashboards, clear the active dashboard ID
      if (globalData.activeDashboardId) {
        console.log("Dashboard: Clearing active dashboard ID as no dashboards exist");
        updateGlobalData({
          activeDashboardId: "",
        });
      }
    }
  }, [
    dashboards,
    globalData.activeDashboardId,
    globalData.activeDashboardGroupId,
    validGroups,
    updateGlobalData,
  ]);

  const settingsData: SettingsProps | undefined = settingsWidget
    ? {
        settings: widgets[settingsWidget]?.settings,
        settingsId: widgets[settingsWidget]?.typeId,
        updateSettings: updateWidgetSetting.bind(null, settingsWidget || ""),
        state: widgets[settingsWidget]?.state,
      }
    : undefined;

  function resetZIndices(newTopId?: string, setMax = false): boolean {
    const ids = Object.keys(widgets ?? {});
    if (ids.length === 0) return false;

    ids.sort((ida, idb) => {
      if (newTopId) {
        if (ida === newTopId) return 1;
        if (idb === newTopId) return -1;
      }

      const zIndexA = widgets[ida]?.position?.zIndex ?? 0;
      const zIndexB = widgets[idb]?.position?.zIndex ?? 0;

      return zIndexA - zIndexB;
    });

    const newIndices = Object.fromEntries(ids.map((id, index) => [id, { zIndex: index + 1 }]));

    if (Object.keys(newIndices).length > 0) {
      updateWidgetPositions(newIndices);
    }

    if (setMax) {
      setMaxZIndex(ids.length);
    }

    return false;
  }

  function addNewWidget(typeId: string, dashboardId: string) {
    const currentDashboard = dashboards.find((dashboard) => dashboard.id === dashboardId);

    const doAdd = (newZIndex: number) => {
      // I think it may help avoid crash the app
      if (!currentDashboard || !currentDashboard.rememberSettings) {
        return;
      }

      const newID = addWidget({
        typeId,
        zIndex: newZIndex,
        rememberSetting: currentDashboard.rememberSettings[typeId],
        dashboardId,
      });

      setMaxZIndex(newZIndex);
      setFocusedWidget(newID);
      setSettingsOpen(false);
    };

    if (maxZIndex + 1 <= MAX_Z_INDEX) {
      doAdd(maxZIndex + 1);
      return;
    }

    resetZIndices();
    doAdd(Object.keys(widgets).length + 1);
  }

  const handleToggleFocus = (isFocus: boolean, widgetId: string) => {
    if (!isFocus) {
      setFocusedWidget(undefined);

      return;
    }

    if (widgetId !== focusedWidget) {
      setFocusedWidget(widgetId);
    }
  };

  function bringToFront(widgetId: string) {
    if (widgetId !== focusedWidget) {
      setFocusedWidget(widgetId);
      setSettingsOpen(false);
    }

    if (widgets[widgetId].position.zIndex === maxZIndex) {
      return;
    }

    if (maxZIndex + 1 <= MAX_Z_INDEX) {
      updateWidgetPositions({ [widgetId]: { zIndex: maxZIndex + 1 } });
      setMaxZIndex(maxZIndex + 1);
      return;
    }

    resetZIndices(widgetId, true);
  }

  //remove all focus when mouse down on the background
  const onPointerDown: React.PointerEventHandler = (e) => {
    if (e.target instanceof HTMLElement && e.target.classList.contains("dashboard")) {
      closeSettings();
    }
  };

  const openWidgetSettings = () => {
    if (focusedWidget) {
      setSettingsWidget(focusedWidget);
      setSettingsOpen(true);
    }
  };

  const closeSettings = () => {
    setFocusedWidget(undefined);
    setSettingsOpen(false);
  };

  const doRemoveWidget = (id: string) => {
    const dashboardId = id.split("|")[1];

    if (!dashboardId) {
      removeWidget(id);
      setFocusedWidget(undefined);
      setSettingsOpen(false);

      return;
    }

    removeWidget(id);
    setFocusedWidget(undefined);
    setSettingsOpen(false);
  };

  const updateRememberSettings = () => {
    if (!focusedWidget || !isSettingsOpen) {
      return;
    }

    const widgetData = widgets[focusedWidget];

    if (!widgetData) {
      return;
    }

    const activeDashboardGroup = findDashboardGroupById(
      globalData.dashboardGroups,
      globalData.activeDashboardGroupId,
    );

    if (!activeDashboardGroup) {
      return;
    }

    const currentDashboard = getValidDashboards(activeDashboardGroup.dashboards).find(
      (dashboard) => dashboard.id === globalData.activeDashboardId,
    );
    if (!currentDashboard) {
      return;
    }
    const shouldRememberSettings = !WIDGETS_WITHOUT_REMEMBER_SETTINGS.includes(widgetData.typeId);

    if (shouldRememberSettings) {
      const newDashboardData = activeDashboardGroup.dashboards.map((dashboard) => {
        if (dashboard?.id === globalData.activeDashboardId) {
          const isOn = currentDashboard.rememberSettings[widgetData.typeId]?.isOn;

          return {
            ...dashboard,
            rememberSettings: {
              ...dashboard.rememberSettings,
              [widgetData.typeId]: {
                isOn: isOn,
                settings: isOn ? widgetData.settings : {},
              },
            },
          };
        }

        return dashboard;
      });
      const newDashboardGroups = getUpdatedDashboardGroups(newDashboardData);
      updateGlobalData({
        dashboardGroups: newDashboardGroups,
      });
    }
  };

  useEffect(() => {
    updateRememberSettings();
  }, [shouldUpdateRememberSettings]);

  const doSpotlightWidget = (id: string) => {
    if (Boolean(spotlightWidget) && spotlightWidget === id) {
      // Unspotlight
      setSpotlightWidget(undefined);
    } else {
      // Spotlight
      setSpotlightWidget(id);
    }
  };

  const getWidgetCounts = () => {
    let counts: { [widgetId: string]: number } = {};

    for (let id of Object.keys(widgets)) {
      const dashboardId = id.split("|")[1];
      const widgetType = widgets[id].typeId;

      if (!dashboardId) {
        continue;
      }

      if (globalData.activeDashboardId !== dashboardId) {
        continue;
      }

      if (counts[widgetType]) {
        counts[widgetType]++;
      } else {
        counts[widgetType] = 1;
      }
    }

    return counts;
  };

  const setDashboardData = (currentId: string, data: DashboardProps) => {
    const selectedGroup = findDashboardGroupById(
      globalData.dashboardGroups,
      globalData.activeDashboardGroupId,
    );
    if (!selectedGroup) {
      return;
    }
    const newDashboards = [...selectedGroup.dashboards];
    const index = newDashboards.findIndex((dash) => dash?.id === currentId);
    data.name = data.name || "New Slide";
    if (index >= 0) {
      newDashboards[index] = data;
      const newDashboardGroups = getUpdatedDashboardGroups(newDashboards);
      updateGlobalData({
        activeDashboardId: currentId,
        dashboardGroups: newDashboardGroups,
      });
    }
  };

  const getUpdatedDashboardGroups = (updatedDashboardData: DashboardProps[]) => {
    return globalData.dashboardGroups.map((group) =>
      group?.id === globalData.activeDashboardGroupId
        ? { ...group, dashboards: updatedDashboardData }
        : group,
    );
  };

  // Determine the correct background URL with better fallback logic
  const backgroundUrl =
    activeDashboard.backgroundName && backgrounds[activeDashboard.backgroundName]
      ? backgrounds[activeDashboard.backgroundName]
      : backgrounds["Purple"];

  // Determine if this is a custom background
  const isCustomBg = activeDashboard.backgroundName === "CUSTOM";

  return (
    <DashboardController
      className="dashboard"
      onPointerDown={onPointerDown}
      isCustomBg={isCustomBg}
      hexBgValue={activeDashboard.hexBgValue}
      backgroundUrl={backgroundUrl}
    >
      {Object.keys(widgets).map((id) => {
        const dashboardId = id.split("|")[1];
        if (dashboardId === globalData.activeDashboardId) {
          return (
            <WidgetErrorBoundary key={id} widgetId={id} onWidgetError={(id) => doRemoveWidget(id)}>
              <Widget
                {...widgets[id]}
                id={id}
                isFocused={id === focusedWidget}
                isSpotlight={id === spotlightWidget}
                setPosition={(position) => updateWidgetPositions({ [id]: position })}
                setState={updateWidgetState.bind(null, id)}
                remove={doRemoveWidget.bind(null, id)}
                bringToFront={bringToFront.bind(null, id)}
                openSettings={openWidgetSettings}
                spotlightWidget={doSpotlightWidget.bind(null, id)}
                updateSettings={updateWidgetSetting.bind(null, id)}
                onToggleFocus={handleToggleFocus}
                isSettingsOpen={isSettingsOpen}
              />
            </WidgetErrorBoundary>
          );
        } else {
          return null;
        }
      })}

      <SettingsBar
        widgetSettings={settingsData?.settings}
        widgetTypeId={settingsData?.settingsId}
        updateSetting={settingsData?.updateSettings}
        closeSettings={closeSettings}
        isOpen={isSettingsOpen}
        widgetState={settingsData?.state}
      />

      <ToolbarContainer
        widgetCounts={getWidgetCounts()}
        addWidget={addNewWidget}
        data={activeDashboard}
        onSetData={setDashboardData.bind(this, globalData.activeDashboardId)}
      />

      <DashboardControls />

      <MultipleDashboardsBox
        activeIndex={Math.max(
          0,
          dashboards.findIndex((dashboard) => dashboard.id === globalData.activeDashboardId),
        )}
        onSetDashboardData={setDashboardData}
      />

      {spotlightWidget && <Spotlight backgroundUrl={backgrounds[activeDashboard.backgroundName]} />}
    </DashboardController>
  );
};

const DashboardController = styled.div<{
  backgroundUrl: string;
  isCustomBg: boolean;
  hexBgValue?: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) => (!props.isCustomBg ? "transparent" : props.hexBgValue)};
  background-image: url(${(props) => (props.isCustomBg ? "none" : props.backgroundUrl)});
  background-size: cover;
  overflow: hidden;
`;

export { Dashboard };
